import type { RootState } from '@restore/store';
import { calcQCTaskCompleted, getAllVisitLabels, makeAllConfigLabelsFlat, } from '../../transformers';
import { createSelector } from '@reduxjs/toolkit';
import { selectQCTaskUploadsListState } from '@restore/QCTaskUploadsList/QCTaskUploadsList.slice';
import { selectQCTaskSeriesWithVisitInfo } from '@restore/QCTaskSeries/QCTaskSeries.selectors';
import { LabelMerged } from '@dynamika-shared/types/imagingLabels';
import { QCTaskPatientVisit, QCVisitWithSeries, ViewerVisit, } from '@dynamika-shared/types/visits';
import { Context } from '@dynamika-shared/types/clinicalTrialContext';
import { ExecutionStatus } from '@dynamika-shared/types/executions';
import { StudyType } from '@dynamika-shared/services/communication';
import { OHIFViewerModeType } from '@dynamika-shared/types/viewerMode';

/**
 *  Returns all visits with labels
 * @param allConfigLabels  - all DICOM labels from configuration flat (merged with modalityName)
 * @param patientVisits  - all visits for patient
 * @param config  - context
 * @param qcVisits  - all visits with series
 * @returns {ViewerVisit[]} - all visits with labels
 */
export const makeJoinedVisitsForQC = (
  allConfigLabels: LabelMerged[],
  patientVisits: QCTaskPatientVisit[],
  config: Context | undefined,
  qcVisits: QCVisitWithSeries[]
): ViewerVisit[] => {
  const configVisits =
    config?.cohorts.map(cohort => cohort.visits).flat() ?? [];

  return qcVisits.map(visit => {
    const { dicomSeries, visitDefinitionId, visitId, studyInstanceUid } = visit;

    const configVisit = visitDefinitionId
      ? configVisits.find(cv => cv.id === visitDefinitionId)
      : undefined;

    const allLabels = getAllVisitLabels(
      allConfigLabels,
      configVisit?.labels ?? [],
      visitDefinitionId
    );

    const patientVisit = patientVisits.find(pv =>
      visitDefinitionId
        ? pv.visitDefinitionId === visitDefinitionId
        : pv.id === visitId
    );

    return {
      dicomSeries,
      visitId,
      studyInstanceUid,
      id: visitDefinitionId,
      visitDefinitionId,
      taskId: patientVisit?.taskId,
      allLabels,
      earlyTerminated: patientVisit?.earlyTerminated,
      type: patientVisit?.type,
      visitName: visitDefinitionId
        ? configVisit?.name
        : patientVisit?.visitName,
    } as ViewerVisit;
  });
};

export const prepareQCStudyState = (s: RootState): StudyType => {
  const {
    auth,
    clinicalTrialContext,
    QCTaskPatient,
    QCTaskSeries,
    QCTask,
    clinicalTrialInventory,
  } = s;

  const allConfigLabels = makeAllConfigLabelsFlat(
    clinicalTrialContext.data?.configuration.labelGroups ?? []
  );

  return {
    clinicalTrialId: clinicalTrialContext.data?.configuration.id ?? '',
    clinicalTrialCode: clinicalTrialContext.data?.configuration.code ?? '',
    clinicalTrialName: clinicalTrialContext.data?.configuration.name ?? '',
    currentUserUid: auth.data?.['dynamika7/user_metadata'].uuid ?? '',
    DICOMResource: QCTask.DICOMResource,
    task: {
      assigneeId: QCTask.data?.assigneeId ?? '',
      id: QCTask.data?.id ?? '',
      patientCode: QCTask.data?.patientCode ?? '',
      patientDateOfBirth: QCTask.data?.patientDateOfBirth ?? '',
      completed: calcQCTaskCompleted(QCTask.data?.status),
    },
    tokenUrl: '',
    viewerMode: OHIFViewerModeType.IMAGING_SPECIALIST,
    visits: makeJoinedVisitsForQC(
      allConfigLabels,
      QCTaskPatient.data?.visits ?? [],
      clinicalTrialContext.data?.configuration,
      QCTaskSeries.data ?? []
    ),
    instruments: clinicalTrialInventory.instruments,
    clinicalTrialInfo: {
      readingName: clinicalTrialContext.data?.configuration?.name ?? '',
      readingCode: QCTask?.data?.patientCode ?? '',
    },
  };
};

export const isViewerAllowedToLoadForQC = createSelector(
  [selectQCTaskSeriesWithVisitInfo, selectQCTaskUploadsListState],
  (visits, uploadsState) => {
    const hasVisits = visits?.length > 0;
    const hasSeries = visits?.some(visit => visit?.dicomSeries?.length);

    const uploadsFailedOrInProgress = uploadsState.data?.every(
      upload =>
        upload.status === ExecutionStatus.FAILED ||
        upload.workflows?.some(
          workflow => workflow.status === ExecutionStatus.IN_PROGRESS
        )
    );

    return hasVisits && hasSeries && !uploadsFailedOrInProgress;
  }
);
